import { createStore, createEvent, createEffect, combine } from 'effector';
import update from 'immutability-helper';
import request from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import createPaginationStore from './pagination';
import { AxiosResponse } from 'axios';
import { getMentionsPageSize } from '@constants/pagination';

import IPost from '@interfaces/IPost';
import { block as blockPost, hidePost } from './posts';

export const reset = createEvent('Mentions Official Account Post store reset');

export const PAGE_SIZE_STORAGE_KEY = 'mentionsOfficialAccountPostPageSize';

const defaultFilters = {
  needChecked: false,
};

export const refresh = createEvent<{ filters?: Record<string, any> }>('Mentions OA Post refresh');
export const init = createEvent('Mentions OA Post init');

const { filters, setFilters } = createFiltersStore({
  listName: 'mentionsOfficialAccountPost',
  deaultValues: defaultFilters,
  resetEvent: reset,
});
export { setFilters };

type SkipUntilDatePaginationParams = {
  pageSize?: number;
  lastDate?: string | null;
};

type GetListParams = { filters?: Record<string, any>; pagination?: SkipUntilDatePaginationParams };

const getPageSize = (pageSize?: number) =>
  getMentionsPageSize(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || pageSize);

const getPostsPage = (result: AxiosResponse<any>): IPost[] => {
  const posts = result.data?.body?.posts;
  return Array.isArray(posts) ? posts : posts?.page ?? [];
};

const getPostMentionDate = (post?: IPost) =>
  post?.mentionUpdatedAt || post?.createdDateTime || null;

export const getList = createEffect<GetListParams | void, AxiosResponse<any>>({
  name: 'getList',
  async handler(params) {
    const p: GetListParams = params || {};
    const paginationParams = p.pagination || {};

    let filtersParams = filters.getState();
    if (p.filters) {
      setFilters(p.filters);
      filtersParams = p.filters;
      paginationParams.lastDate = null;
    }

    const { pageSize, lastDate = null } = paginationParams;

    const response: AxiosResponse<any> = await request(api.mentionsOfficialAccount.getPost, {
      data: {
        includeChecked: !!filtersParams.needChecked,
        pagination: {
          type: 'SKIP_UNTIL_DATE',
          pageSize: getPageSize(pageSize),
          lastDate,
        },
      },
    });

    return response;
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'mentionsOfficialAccountPost',
  resetEvent: reset,
  bodyPath: ['data', 'body', 'posts'],
});

export { setPagination };

export const checkAllPosts = createEffect<void, AxiosResponse<any>>({
  name: 'checkAllPosts',
  async handler() {
    return request(api.mentionsOfficialAccount.checkAllPosts);
  },
});

export const checkPost = createEffect<
  { postId: IPost['id']; preloaderId?: string },
  AxiosResponse<any>
>({
  name: 'checkPost',
  async handler({ postId }) {
    return request(api.mentionsOfficialAccount.checkPost, {
      urlParams: { postId },
    });
  },
});

export const replyToPost = createEffect<
  { postId: string; text: string; replyAccountId: string; images?: File[] },
  AxiosResponse<any>
>({
  name: 'replyToPost',
  async handler(params) {
    const formData = new FormData();
    (params.images || []).forEach((file) => formData.append('images', file));

    return request(api.mentionsOfficialAccount.replyToPost, {
      urlParams: { postId: params.postId },
      params: { text: params.text, replyAccountId: params.replyAccountId },
      data: formData,
    });
  },
});

const preloaders = createPreloadersStore([
  getList,
  checkPost,
  checkAllPosts,
  replyToPost,
  blockPost,
  hidePost,
]);

export const updateListHandler = (state: IPost[], { params: postId }: { params: IPost['id'] }) => {
  const index = state.findIndex((p) => p.id === postId);
  return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
};

const list = createStore<IPost[]>([])
  .on(getList.done, (state, { params, result }) => {
    const page = getPostsPage(result);
    const filteredPage = page.filter((post) => !post.author?.isOfficial);

    return params && params.pagination?.lastDate ? [...state, ...filteredPage] : filteredPage;
  })
  .on(checkAllPosts.done, (state) => {
    const filtersParams = filters.getState();
    return filtersParams.needChecked ? state : [];
  })
  .on(checkPost.done, (state, { params }) => {
    const postId = params.postId;
    const index = state.findIndex((p) => String(p.id) === String(postId));
    if (filters.getState().needChecked && index >= 0) {
      return update(state, { [index]: { $merge: { isChecked: true } } });
    }
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .on(replyToPost.done, (state, { params }) => {
    const postId = params.postId;
    const index = state.findIndex((p) => String(p.id) === String(postId));
    if (filters.getState().needChecked && index >= 0) {
      return update(state, { [index]: { $merge: { isChecked: true } } });
    }
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .on(blockPost.done, (state, { params }) => {
    const postId = params.postId;
    const index = state.findIndex((p) => String(p.id) === String(postId));
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .on(hidePost.done, (state, { params }) => {
    const postId = params.postId;
    const index = state.findIndex((p) => String(p.id) === String(postId));
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .reset(reset);

const hasMore = createStore(true)
  .on(getList.done, (state, { params, result }) => {
    const page = getPostsPage(result);
    const pageSize = getPageSize(params ? params.pagination?.pageSize : undefined);
    return page.length >= pageSize && !!getPostMentionDate(page[page.length - 1]);
  })
  .reset(reset);

const nextLastDate = createStore<string | null>(null)
  .on(getList.done, (state, { result }) => {
    const page = getPostsPage(result);
    return getPostMentionDate(page[page.length - 1]);
  })
  .reset(reset);

init.watch(() => {
  getList();
});

refresh.watch((params) => {
  getList({ filters: params.filters });
});

const mentionsOfficialAccountPost = combine({
  preloaders,
  list,
  hasMore,
  nextLastDate,
  filters,
  pagination,
  setPagination,
});

export default mentionsOfficialAccountPost;
